import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAdministrationAPI } from "../AdministrationAPI";
import { useModalContext } from "../components/ModalProvider";
import { useCustomerTheme } from "../Theme";
import Section from "../components/Section";
import Metric from "../components/Metric";
import ReactApexChart from "react-apexcharts";
import dayjs from "dayjs";

import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Nutzerverhalten — anonymous user-operation telemetry (platform admins only). Filters by
 * operation type, customer (or all) and interval; shows mean duration + count and two bar charts
 * (mean duration and count) bucketed per hour (today) or per day (longer ranges). All figures are
 * aggregates — no user identities are surfaced.
 */
type Interval = "today" | "10d" | "30d" | "90d";

interface SelectOption {
  key: string;
  text: string;
}

const TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Berlin";
const DAY_MS = 24 * 60 * 60 * 1000;

// Radix-`Select` verbietet den leeren Wert; „Alle Kunden“ (intern '') bekommt
// deshalb einen Stellvertreter, der nur in der Auswahl existiert.
const ALL_CUSTOMERS = "__all__";

function rangeFor(interval: Interval): { from: Date; to: Date; bucket: "hour" | "day" } {
  const to = new Date();
  if (interval === "today") {
    const from = new Date();
    from.setHours(0, 0, 0, 0);
    return { from, to, bucket: "hour" };
  }
  const days = interval === "10d" ? 10 : interval === "30d" ? 30 : 90;
  return { from: new Date(to.getTime() - days * DAY_MS), to, bucket: "day" };
}

function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return "—";
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)} s`;
  return `${Math.floor(s / 60)} min ${Math.round(s % 60)} s`;
}

const bucketLabel = (b: string, bucket: "hour" | "day"): string =>
  bucket === "hour" ? dayjs(b).format("HH:mm") : dayjs(b).format("DD.MM.");

export default function UserBehaviorPanel() {
  // Keys live under administrationPage.userBehaviorPanel.* (panel-naming convention); the prefix
  // lets the calls below stay as t("userBehaviorPanel.…").
  const { t } = useTranslation(undefined, { keyPrefix: "administrationPage" });
  const { pushError } = useModalContext() as any;
  const { theme } = useCustomerTheme();
  const fieldId = useId();

  // useAdministrationAPI() returns fresh function refs every render, so keep them in a ref and
  // never put them in effect deps — otherwise the effects re-run every render (flicker / loop).
  const api = useAdministrationAPI();
  const apiRef = useRef(api);
  apiRef.current = api;

  const [operation, setOperation] = useState<string>("assetCapture");
  const [tenant, setTenant] = useState<string>(""); // '' = Alle
  const [interval, setIntervalValue] = useState<Interval>("30d");
  const [customers, setCustomers] = useState<Array<{ id: string; name: string }>>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const operationId = `${fieldId}-operation`;
  const customerId = `${fieldId}-customer`;
  const intervalId = `${fieldId}-interval`;

  // Load the customer list once.
  useEffect(() => {
    apiRef.current
      .getCustomers()
      .then((cs: any[]) =>
        setCustomers((cs || []).map((c: any) => ({ id: String(c.id ?? c._id), name: c.name })))
      )
      .catch(() => setCustomers([]));
  }, []);

  // Re-fetch stats only when a filter actually changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const { from, to, bucket } = rangeFor(interval);
        const result = await apiRef.current.getUserOperationStats(
          operation,
          tenant || null,
          from,
          to,
          bucket,
          TZ
        );
        if (!cancelled) setStats(result);
      } catch (error) {
        if (!cancelled) {
          pushError(error);
          setStats(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operation, tenant, interval]);

  const bucket = rangeFor(interval).bucket;
  const series: any[] = stats?.series ?? [];
  const categories = useMemo(
    () => series.map((s) => bucketLabel(s.bucket, bucket)),
    [series, bucket]
  );

  const operationOptions: SelectOption[] = [
    { key: "assetCapture", text: t("userBehaviorPanel.operationValue.assetCapture") },
  ];
  const customerOptions: SelectOption[] = [
    { key: ALL_CUSTOMERS, text: t("userBehaviorPanel.allCustomers") }, // "Alle" stays first
    ...customers
      .map((c) => ({ key: c.id, text: (c.name || c.id).trim() }))
      .sort((a, b) => a.text.localeCompare(b.text, "de", { sensitivity: "base" })),
  ];
  const intervalOptions: SelectOption[] = [
    { key: "today", text: t("userBehaviorPanel.intervalValue.today") },
    { key: "10d", text: t("userBehaviorPanel.intervalValue.last10") },
    { key: "30d", text: t("userBehaviorPanel.intervalValue.last30") },
    { key: "90d", text: t("userBehaviorPanel.intervalValue.last90") },
  ];

  const barOptions = (): any => ({
    chart: { type: "bar", toolbar: { show: false } },
    plotOptions: { bar: { columnWidth: "60%", borderRadius: 3 } },
    xaxis: { categories },
    colors: [theme.primary],
    dataLabels: { enabled: false },
    noData: { text: t("userBehaviorPanel.noData") },
  });

  return (
    <div className="flex w-full min-w-0 max-w-[1400px] flex-col gap-4">
      <div className="flex min-w-0 flex-wrap items-end gap-4">
        <div className="flex w-[240px] min-w-0 max-w-full flex-col gap-1.5">
          <Label htmlFor={operationId}>{t("userBehaviorPanel.operation")}</Label>
          <Select value={operation} onValueChange={(value) => setOperation(value)}>
            <SelectTrigger id={operationId}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-w-[calc(100vw-2rem)]">
              {operationOptions.map((option) => (
                <SelectItem key={option.key} value={option.key} className="whitespace-normal">
                  {option.text}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex w-[240px] min-w-0 max-w-full flex-col gap-1.5">
          <Label htmlFor={customerId}>{t("userBehaviorPanel.customer")}</Label>
          <Select
            value={tenant === "" ? ALL_CUSTOMERS : tenant}
            onValueChange={(value) => setTenant(value === ALL_CUSTOMERS ? "" : value)}
          >
            <SelectTrigger id={customerId}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-w-[calc(100vw-2rem)]">
              {customerOptions.map((option) => (
                <SelectItem key={option.key} value={option.key} className="whitespace-normal">
                  {option.text}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex w-[240px] min-w-0 max-w-full flex-col gap-1.5">
          <Label htmlFor={intervalId}>{t("userBehaviorPanel.interval")}</Label>
          <Select
            value={interval}
            onValueChange={(value) => setIntervalValue((value as Interval) ?? "30d")}
          >
            <SelectTrigger id={intervalId}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-w-[calc(100vw-2rem)]">
              {intervalOptions.map((option) => (
                <SelectItem key={option.key} value={option.key} className="whitespace-normal">
                  {option.text}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="flex min-w-0 flex-col gap-4" role="status" aria-live="polite">
          <span className="sr-only">{t("userBehaviorPanel.loading")}</span>
          <Skeleton className="h-[136px] w-full" />
          <Skeleton className="h-[356px] w-full" />
          <Skeleton className="h-[356px] w-full" />
        </div>
      ) : (
        <>
          <Section title="">
            <div className="flex min-w-0 flex-wrap gap-8">
              <Metric
                size="large"
                color={theme.secondary}
                title={t("userBehaviorPanel.avgDuration")}
                value={formatDuration(stats?.avgDurationMs)}
              />
              <Metric
                size="large"
                color={theme.tertiary}
                title={t("userBehaviorPanel.count")}
                value={String(stats?.count ?? 0)}
              />
            </div>
          </Section>

          <Section title={t("userBehaviorPanel.durationChart")}>
            <div className="min-w-0">
              <ReactApexChart
                options={barOptions()}
                series={[
                  {
                    name: t("userBehaviorPanel.avgDurationSeconds"),
                    data: series.map((s) => Math.round((s.avgDurationMs ?? 0) / 100) / 10),
                  },
                ]}
                type="bar"
                height={300}
              />
            </div>
          </Section>

          <Section title={t("userBehaviorPanel.countChart")}>
            <div className="min-w-0">
              <ReactApexChart
                options={barOptions()}
                series={[{ name: t("userBehaviorPanel.count"), data: series.map((s) => s.count ?? 0) }]}
                type="bar"
                height={300}
              />
            </div>
          </Section>
        </>
      )}
    </div>
  );
}
